import React, { useState } from "react";
import styles from "./loginScreen.module.css";
import { Box, Button, Card, CardActionArea, CardContent, FormControl, Link, TextField, Typography } from "@mui/material";
import { useAuthContext } from "../../contexts/auth";
import { TenantData, TokenRequest } from "../../types/auth";
import ApartmentIcon from '@mui/icons-material/Apartment';
import { Trans, useTranslation } from "react-i18next";

export const LoginScreen = () => {
  const { t } = useTranslation();
  const {
    getLoginCode,
    login,
    logout,
    setTenant,
    loadingState,
    errorState,
    isSecure,
  } = useAuthContext();

  const defaultLoginData = { email: "", pin_code: "" };
  const [loginData, setLoginData] = useState(defaultLoginData);
  const [tenants, setTenants] = useState<TenantData[]>([]);
  const [loginCodeSent, setLoginCodeSent] = useState<boolean>(false);
  const [tokenRequestData, setTokenRequestData] = useState<TokenRequest>({
    email: "",
    tenant_selection_pin_code: "",
  });

  const handleLoginSubmit = async () => {
    const { success } = await getLoginCode({ email: loginData.email });
    if (success) setLoginCodeSent(true);
  };
  const handlePinCodeSubmit = async () => {
    const { data } = await login(loginData);
    if (data) {
      setTenants(data.tenants);
      setTokenRequestData({
        email: loginData.email,
        tenant_selection_pin_code: data.tenant_selection_code,
      });
    }
  };
  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
  };
  const handleLogout = () => {
    setTenants([]);
    setLoginCodeSent(false);
    setLoginData(defaultLoginData);
    logout();
  };

  const handleTenantSelect = (tenantId: string) => setTenant(tokenRequestData, tenantId);

  const renderLoginContent = () => (
    <form action={handleLoginSubmit}>
      <FormControl variant="standard" className={styles.formControl} fullWidth>
        <div className={styles.headerTitle}>{t("Log in")}</div>
        <Box className={styles.inputRow}>
          <Box sx={{ width: '100%' }}>
            <TextField name="email" type="email" label={t("Email address")} size="small" sx={{ width: '100%' }} value={loginData.email} onChange={handleLoginChange} required />
            {errorState.login?.email && (
              <div className={styles.errorMessage}>{errorState.login.email[0].message}</div>
            )}
          </Box>
          <Button
            variant="contained"
            color="success"
            type="submit"
            onClick={handleLoginSubmit}
            loading={loadingState.login}
            fullWidth
          >
            {t("Send pin code")}
          </Button>
        </Box>
      </FormControl>
    </form>
  );

  const renderPinCodeContent = () => (
    <form action={handlePinCodeSubmit}>
      <FormControl variant="standard" className={styles.formControl} fullWidth>
        <div className={styles.headerTitle}>{t("Enter pin code from your email")}</div>
        <Box className={styles.inputRow}>
          <Box sx={{ width: "100%" }}>
            <TextField
              name="pin_code"
              type="text"
              label={t("Pin code")}
              size="small"
              sx={{ width: '100%' }}
              value={loginData.pin_code}
              onChange={handleLoginChange}
              required
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }
              }}
            />
            {errorState.login?.pin_code && (
              <div className={styles.errorMessage}>{errorState.login.pin_code[0].message}</div>
            )}
          </Box>
          <Button
            variant="contained"
            color="success"
            type="submit"
            onClick={handlePinCodeSubmit}
            loading={loadingState.login}
            fullWidth
          >
            {t("Submit login")}
          </Button>
        </Box>
      </FormControl>
    </form>
  );

  const renderTenantContent = () => {
    return (
      <>
        <div className={styles.resetHeaderTitle}>{t("Select Workspace")}</div>
        <p>{t("You have more than one available workspace. Please select one.")}</p>
        <FormControl variant="standard" className={styles.formControl}>
          {tenants.map((tenant, key) => (
            <Card key={key}>
              <CardActionArea onClick={() => handleTenantSelect(tenant.id)}>
                <CardContent className={styles.cardContent}>
                  <Box className={styles.logoBox}>
                    { tenant.logo ? (
                      <img src={tenant.logo} alt="" />
                      ) : <ApartmentIcon fontSize="large" />
                    }
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" lineHeight={1} component="div">
                      {tenant.name}
                    </Typography>
                    {tenant.website && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {tenant.website}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
            <p className={styles.logoutParagraph}>
              <Trans
                i18nKey="Want to use another account? <clickHereLink>Click here</clickHereLink> to logout."
                components={{
                  clickHereLink: <Link href="#" onClick={() => handleLogout() } />,
                }}
              />
            </p>
        </FormControl>
      </>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.loginBox}>
        {tenants.length > 0
          ? renderTenantContent()
          : loginCodeSent
          ? renderPinCodeContent()
          : !isSecure
          ? renderLoginContent()
          : null}
      </Box>
    </Box>
  );
};
